/**
 * useAuth — hook auth completo.
 * - Carica /auth/me al mount se c'è un token
 * - Espone login(), logout(), setupFirstAdmin()
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { type User, useAuthStore } from "../lib/auth-store";

interface TokenPair {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export function useAuth() {
  const { accessToken, user, setTokens, setUser, clear, isAuthenticated } =
    useAuthStore();
  const navigate = useNavigate();

  // Bootstrap: se ho token ma non user, carico /me
  useEffect(() => {
    if (accessToken && !user) {
      api
        .get<User>("/auth/me")
        .then((res) => setUser(res.data))
        .catch(() => clear()); // token invalido → pulisci
    }
  }, [accessToken, user, setUser, clear]);

  async function login(email: string, password: string) {
    const res = await api.post<TokenPair>("/auth/login", { email, password });
    setTokens(res.data.access_token, res.data.refresh_token);
    const me = await api.get<User>("/auth/me");
    setUser(me.data);
    navigate("/", { replace: true });
  }

  async function setupFirstAdmin(
    email: string,
    password: string,
    nome_completo: string,
  ) {
    const res = await api.post<TokenPair>("/setup/first-admin", {
      email,
      password,
      nome_completo,
    });
    setTokens(res.data.access_token, res.data.refresh_token);
    const me = await api.get<User>("/auth/me");
    setUser(me.data);
    navigate("/", { replace: true });
  }

  /** Ricarica /auth/me (es. dopo un cambio di lega). */
  async function ricaricaMe() {
    const me = await api.get<User>("/auth/me");
    setUser(me.data);
    return me.data;
  }

  async function logout() {
    const refresh = useAuthStore.getState().refreshToken;
    try {
      if (refresh) await api.post("/auth/logout", { refresh_token: refresh });
    } catch {
      // ignore — logout idempotente
    }
    clear();
    navigate("/login", { replace: true });
  }

  return {
    user,
    isAuthenticated: isAuthenticated(),
    login,
    logout,
    setupFirstAdmin,
    ricaricaMe,
  };
}
